import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export function UsageIndicatorServer(): null {
  return null;
}

export function UsageIndicatorClient(): import('react/jsx-runtime').JSX.Element {
  const navigate = useNavigate();
  const [usage, setUsage] = useState<{ used: number; limit: number } | null>(null);

  useEffect((): void => {
    void fetch('/api/orgs/active/usage')
      .then((r): Promise<unknown> | null => (r.ok ? (r.json() as Promise<unknown>) : null))
      .then((data): void => {
        if (Boolean(data)) setUsage(data as { used: number; limit: number });
      })
      .catch((): void => {});
  }, []);

  const used = usage?.used ?? 0;
  const limit = usage?.limit ?? 25000;
  const percentage = Math.min((used / limit) * 100, 100);

  let colorClass = 'bg-primary';
  if (percentage >= 100) colorClass = 'bg-destructive';
  else if (percentage >= 80) colorClass = 'bg-yellow-500';

  return (
    <TooltipProvider delay={300}>
      <Tooltip>
        <TooltipTrigger
          onClick={() => {
            navigate('/usage');
          }}
          className="flex items-center gap-3 px-3 py-1.5 rounded-md hover:bg-muted/50 transition-colors cursor-pointer"
        >
          <div className="flex flex-col gap-1 items-end pt-[0.2rem]">
            <span className="text-[10px] uppercase font-medium text-muted-foreground tracking-wider leading-none">
              Usage
            </span>
            <span className="text-[11px] font-medium leading-none">
              {used.toLocaleString()} / {limit.toLocaleString()}
            </span>
          </div>
          <div className="h-1.5 w-16 bg-gradient-to-r from-muted to-muted/80 rounded-full overflow-hidden shrink-0 mt-2">
            <div
              className={cn('h-full transition-all duration-500 ease-in-out', colorClass)}
              style={{ width: `${percentage}%` }}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="end" className="text-xs">
          {percentage >= 100
            ? 'Limit reached! Upgrade plan to resume executions.'
            : percentage >= 80
              ? 'Approaching limit. Consider upgrading your plan.'
              : `${used.toLocaleString()} / ${limit.toLocaleString()} executions used this billing cycle.`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
